import { Badge } from "@bowfast/ui/components/badge";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { Separator } from "@bowfast/ui/components/separator";
import { Switch } from "@bowfast/ui/components/switch";
import { cn } from "@bowfast/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangleIcon, ArrowRightIcon, CheckCircle2Icon, SettingsIcon, UsersIcon } from "lucide-react";
import { useState } from "react";
import { fadeUp } from "~/lib/animations";
import { getSignalConfig } from "~/pages/notifications/notifications.config";
import type { ActiveExpedition, Signal } from "~/pages/notifications/notifications.types";
import { formatTime } from "~/pages/notifications/notifications.utils";

// ── ActionSummary ─────────────────────────────────────────────────────────────

function ActionSummary({ signals }: Readonly<{ signals: Signal[] }>) {
  const actionable = signals.filter((s) => s.actionable && !s.readAt);
  const now = new Date();

  if (actionable.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-shallow-50/50 px-3 py-2.5 dark:bg-shallow-900/20">
        <CheckCircle2Icon className="size-4 text-shallow-500" />
        <span className="text-sm font-medium text-shallow-700 dark:text-shallow-400">
          Nothing waiting for your response
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold uppercase tracking-[0.06em] text-muted-foreground">Awaiting Response</p>
      {actionable.map((s) => {
        const cfg = getSignalConfig(s.type);
        const SIcon = cfg.Icon;
        return (
          <div
            key={s.id}
            className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:bg-muted/30"
          >
            <div className={cn("flex size-6 shrink-0 items-center justify-center rounded-md", cfg.iconBg)}>
              <SIcon className={cn("size-3", cfg.iconColor)} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[0.8125rem] font-medium leading-tight">{s.title}</p>
              <p className="text-[0.6875rem] text-muted-foreground">{formatTime(s.createdAt, now)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── ExpeditionPulse ───────────────────────────────────────────────────────────

function ExpeditionPulse({ expedition }: Readonly<{ expedition: ActiveExpedition }>) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.06em] text-muted-foreground">Active Voyage</p>
      <Link
        to="/expeditions/$expeditionSlug"
        params={{ expeditionSlug: expedition.slug }}
        className="group flex gap-3 rounded-lg border border-border bg-card p-3 transition-colors hover:border-abyss-300/40 hover:bg-muted/30"
      >
        <div className="flex flex-col items-center justify-center rounded-lg bg-abyss-50 px-3 py-1.5 dark:bg-abyss-800">
          <span className="font-heading text-lg font-bold leading-none text-abyss-500">{expedition.dayNum}</span>
          <span className="text-[0.625rem] font-medium uppercase text-muted-foreground">Day</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold">{expedition.name}</p>
            <Badge variant="active" dotted pulse className="text-[0.625rem]">
              Live
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{expedition.route}</p>
          <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span>
              {expedition.flag} {expedition.vessel}
            </span>
            <Separator orientation="vertical" className="h-3!" />
            <span>{expedition.crewCount} crew</span>
          </div>
        </div>
        <ArrowRightIcon className="mt-2 size-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-abyss-500" />
      </Link>
    </div>
  );
}

// ── QuietToggles ──────────────────────────────────────────────────────────────

function QuietToggles() {
  const [muted, setMuted] = useState<Record<string, boolean>>({});

  const toggles = [
    { key: "critical", label: "Critical alerts", Icon: AlertTriangleIcon, color: "text-coral-500" },
    { key: "crew", label: "Crew updates", Icon: UsersIcon, color: "text-abyss-500" },
    { key: "system", label: "System messages", Icon: SettingsIcon, color: "text-slate-500" },
  ];

  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.06em] text-muted-foreground">Quick Silence</p>
      <div className="space-y-2">
        {toggles.map((t) => {
          const TIcon = t.Icon;
          return (
            <div key={t.key} className="flex items-center justify-between gap-2 py-1">
              <div className="flex items-center gap-2">
                <TIcon className={cn("size-3.5", t.color)} />
                <span className="text-[0.8125rem] font-medium">{t.label}</span>
              </div>
              <Switch
                size="sm"
                checked={!muted[t.key]}
                onCheckedChange={(checked) => setMuted((prev) => ({ ...prev, [t.key]: !checked }))}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SignalPanel ───────────────────────────────────────────────────────────────

interface SignalPanelProps {
  signals: Signal[];
  activeExpedition?: ActiveExpedition;
}

export function SignalPanel({ signals, activeExpedition }: Readonly<SignalPanelProps>) {
  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}>
      <Card className="sticky top-24">
        <CardContent className="space-y-5 p-4">
          <ActionSummary signals={signals} />
          {activeExpedition && (
            <>
              <Separator />
              <ExpeditionPulse expedition={activeExpedition} />
            </>
          )}
          <Separator />
          <QuietToggles />
        </CardContent>
      </Card>
    </motion.div>
  );
}
